import { FunctionComponent, memo, useContext } from "react";
import { useQueryClient } from "react-query";
import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { axios } from "utilities/axios";
import toast from "react-hot-toast";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import { getTimeToRead } from "@edgetier/utilities";
import { getServerError } from "utilities/get-server-error";
import { Button } from "@edgetier/components";
import { IUser, IUsersGetResponse, Route } from "@edgetier/watchtower-types";
import { IProps } from "./user-remove-action.types";
import "./user-remove-action.scss";

/**
 * A menu action, when clicked opens a confirmation modal to remove a user
 * @param props.user The user intented to be removed
 */
const UserRemoveAction: FunctionComponent<IProps> = ({ user }) => {
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });
    const queryClient = useQueryClient();

    /**
     * POST request to remove the user
     */
    const removeUser = async (user: IUser) => {
        try {
            await axios.delete(Route.User.replace(":userId", user.userId));

            // Remove user from cache
            queryClient.setQueriesData<IUsersGetResponse>(Route.Users, (cachedUsers?: IUsersGetResponse) => {
                const pagination = cachedUsers?.pagination ?? { nextPage: null, previousPage: null };
                const updatedUsers = (cachedUsers?.items ?? []).filter((queryUser) => user.userId !== queryUser.userId);
                return {
                    pagination,
                    items: updatedUsers,
                };
            });

            const successMessage = `Successfully removed ${user.firstName} ${user.surname}`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
        } catch (error) {
            toast.error(getServerError(error));
        } finally {
            closeModal();
        }
    };
    return (
        <>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Remove User</h2>
                </ModalHeader>
                <ModalContent>
                    <div className="user-remove-action">
                        <p>
                            Are you sure you want to remove {user.firstName} {user.surname}?
                        </p>
                    </div>
                    <div className="user-remove-action__buttons">
                        <Button icon={faTrash} styleName="negative" onClick={() => removeUser(user)}>
                            Remove User
                        </Button>
                    </div>
                </ModalContent>
            </Modal>
            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faTrash}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Remove
            </EllipsisMenuItem>
        </>
    );
};

export default memo(UserRemoveAction);
